"use client";

import { useMemo, useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogBackdrop,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ArrowUpTrayIcon } from "@heroicons/react/20/solid";
import LogoUrl from "../../Assets/IconSVG/SolidarityLogo.svg?url";
import { CreateUser } from "../API/PermissionServices";

// --- helpers ---
function emptyAccess() {
  return { parents: new Set(), children: new Set() };
}

export default function CreateUserDrawer({
  lookupSections = [],
  setLoading,
  fetchSolidarityUsers,
}) {
  const [open, setOpen] = useState(false);

  // Profile fields for NEW user
  const [client, setClient] = useState("");
  const [email, setEmail] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [ext, setExt] = useState("");
  const [requestLimit, setRequestLimit] = useState("");
  const [paymentAccess, setPaymentAccess] = useState("OwnOnly"); // "OwnOnly" | "AllView" | "AllClaims"
  const [tempPassword, setTempPassword] = useState("");

  // Multi-select for PaymentRequestType
  const [paymentRequestTypes, setPaymentRequestTypes] = useState([]);
  const togglePaymentRequestType = (value) => {
    setPaymentRequestTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // Avatar upload state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarError, setAvatarError] = useState("");
  const fileInputRef = useRef(null);

  // Access selections (new user => nothing selected by default)
  const baseAccess = useMemo(() => emptyAccess(), []);
  const [selectedParents, setSelectedParents] = useState(
    new Set(baseAccess.parents)
  );
  const [selectedChildren, setSelectedChildren] = useState(
    new Set(baseAccess.children)
  );

  // Controlled accordion: which parent rows are open
  const [openRows, setOpenRows] = useState(new Set());
  const toggleRow = (id) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Confirms
  const [confirmParent, setConfirmParent] = useState(null);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  // Payment Requests
  const paymentRequestsParentId = useMemo(() => {
    const p = lookupSections.find((s) => s.productName === "Payment Requests");
    return p?.id ?? null;
  }, [lookupSections]);
  const isPaymentRequestsSelected =
    !!paymentRequestsParentId && selectedParents.has(paymentRequestsParentId);
  const [paymentSettingsOpen, setPaymentSettingsOpen] = useState(false);

  const selectedCounts = useMemo(
    () => ({ parents: selectedParents.size, children: selectedChildren.size }),
    [selectedParents, selectedChildren]
  );

  // ---------- Avatar handling ----------
  const MAX_MB = 5;
  const ACCEPTED = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
    "image/svg+xml",
  ];

  const readPreview = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const handleFile = async (file) => {
    setAvatarError("");
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) {
      setAvatarError("Unsupported file type. Use JPG, PNG, WEBP, GIF, or SVG.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setAvatarError(`Image is too large. Max ${MAX_MB}MB.`);
      return;
    }
    const previewUrl = await readPreview(file);
    setAvatarFile(file);
    setAvatarPreview(previewUrl);
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleFile(file);
  };

  const clearAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatarError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drag & drop polish
  const [dragActive, setDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    setDragActive(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      setDragActive(false);
      dragCounterRef.current = 0;
    }
  };
  const onDropNice = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounterRef.current = 0;
    const file = e.dataTransfer?.files?.[0];
    if (file) await handleFile(file);
  };

  // ---------- Access toggles ----------
  const tryToggleParent = (parent) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const isSelected = selectedParents.has(parent.id);

    if (isSelected) {
      const childIds = (parent.children ?? []).map((c) => c.id);
      const hasAnySelectedChild = childIds.some((cid) =>
        selectedChildren.has(cid)
      );
      if (hasAnySelectedChild) {
        setConfirmParent(parent);
      } else {
        setSelectedParents((prev) => {
          const next = new Set(prev);
          next.delete(parent.id);
          return next;
        });
      }
    } else {
      setSelectedParents((prev) => new Set(prev).add(parent.id));
      setOpenRows((prev) => new Set(prev).add(parent.id));
    }
  };

  const confirmTurnOffParent = () => {
    if (!confirmParent) return;
    setSelectedParents((prev) => {
      const next = new Set(prev);
      next.delete(confirmParent.id);
      return next;
    });
    setSelectedChildren((prev) => {
      const next = new Set(prev);
      for (const c of confirmParent.children || []) next.delete(c.id);
      return next;
    });
    setConfirmParent(null);
  };
  const cancelTurnOffParent = () => setConfirmParent(null);

  const toggleChild = (childId, parentId) => {
    setSelectedChildren((prev) => {
      const next = new Set(prev);
      const isSelected = next.has(childId);
      if (isSelected) {
        next.delete(childId);
      } else {
        next.add(childId);
        setSelectedParents((prevP) => {
          const np = new Set(prevP);
          np.add(parentId);
          return np;
        });
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedParents(new Set());
    setSelectedChildren(new Set());
  };

  // When "Payment Requests" becomes unselected, clear its fields
  useEffect(() => {
    if (!paymentRequestsParentId) return;
    const isSelected = selectedParents.has(paymentRequestsParentId);
    if (!isSelected) {
      setRequestLimit("");
      setPaymentAccess("OwnOnly");
      setPaymentRequestTypes([]);
      setPaymentSettingsOpen(false);
    }
  }, [selectedParents, paymentRequestsParentId]);

  // ---------- Save flow ----------
  const handleCancel = () => {
    setClient("");
    setEmail("");
    setDepartment("");
    setPosition("");
    setPhoneNumber("");
    setExt("");
    setRequestLimit("");
    setPaymentAccess("OwnOnly");
    setPaymentRequestTypes([]);
    setTempPassword("");
    clearAvatar();
    clearSelection();
    setConfirmParent(null);
    setConfirmSaveOpen(false);
    setPaymentSettingsOpen(false);
    setOpen(false);
  };

  const handleSaveClick = () => setConfirmSaveOpen(true);

  const handleSaveConfirm = async () => {
    if (setLoading) setLoading(true);
    const token = sessionStorage.getItem("token") || "";

    const hubSectionIds = new Set([...selectedParents, ...selectedChildren]);
    const HubSection = Array.from(hubSectionIds).map((id) => ({ Id: id }));

    const PaymentRequestType = paymentRequestTypes.map((t) => ({ Type: t }));

    const normalizedMobile = (phoneNumber || "").trim();
    const normalizedExt = (ext || "").trim();

    const limitNum =
      requestLimit === "" || isNaN(Number(requestLimit)) ? 0 : Number(requestLimit);

    const payload = {
      UserName: client,
      Email: email,
      PasswordHashed: tempPassword || "Solid@1234",
      Department: department,
      Position: position,
      Token: token,
      PhoneNumber: normalizedMobile,
      Ext: normalizedExt,
      RequestLimit: limitNum,
      PaymentRequestsAccessLevel: paymentAccess,
      HubSection,
      PaymentRequestType,
      ProfilePic: avatarPreview || null,
    };

    try {
      const result = await CreateUser(payload);

      if (!result) {
        alert("Create failed. No response from server.");
        return;
      }
      if (result.statusCode && result.statusCode >= 400) {
        console.error("Create error:", result);
        alert(
          `Create failed (${result.statusCode}): ${
            result.message || "Unknown error"
          }`
        );
        return;
      }

      setConfirmSaveOpen(false);
      setOpen(false);
      if (setLoading) setLoading(false);
      if (fetchSolidarityUsers) fetchSolidarityUsers();
      handleCancel();
      alert("User created successfully.");
    } catch (err) {
      if (setLoading) setLoading(false);
      console.error(err);
      alert("Network or server error while creating the user.");
    }
  };

  const handleSaveCancel = () => setConfirmSaveOpen(false);

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        className="rounded-md bg-gray-950/5 px-2.5 py-1.5 text-sm font-semibold text-gray-900 hover:bg-gray-950/10"
      >
        Create user
      </button>

      {/* Main Drawer */}
      <Dialog open={open} onClose={handleCancel} className="relative z-10">
        <div className="fixed inset-0" />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
              >
                <div className="relative flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                  {/* Header */}
                  <div className="px-4 py-6 sm:px-6">
                    <div className="flex items-start justify-between">
                      <DialogTitle className="text-base font-semibold text-gray-900">
                        Create User
                      </DialogTitle>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Main */}
                  <div className="divide-y divide-gray-200">
                    {/* Banner + Avatar */}
                    <div className="pb-6">
                      <div
                        className="h-24 sm:h-20 lg:h-28 bg-no-repeat bg-center bg-[length:140px] bg-neutral-50"
                        style={{ backgroundImage: `url(${LogoUrl})` }}
                      />
                      <div className="-mt-12 flow-root px-4 sm:-mt-8 sm:flex sm:items-end sm:px-6 lg:-mt-16">
                        <div>
                          <div className="-m-1 flex">
                            <div className="inline-flex overflow-hidden rounded-lg border-4 border-white">
                              {/* Avatar drop zone */}
                              <label
                                htmlFor="avatar-input"
                                onDrop={onDropNice}
                                onDragEnter={handleDragEnter}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                className={[
                                  "relative size-24 sm:size-40 lg:size-48 flex items-center justify-center",
                                  "bg-gray-50 outline outline-1 -outline-offset-1 outline-black/5 cursor-pointer",
                                  "transition-all duration-200 rounded-md",
                                  dragActive ? "ring-2 ring-indigo-300 bg-indigo-50/70" : "",
                                ].join(" ")}
                                title="Click or drag & drop an image"
                              >
                                {avatarPreview ? (
                                  <img
                                    alt="Avatar preview"
                                    src={avatarPreview}
                                    className="absolute inset-0 size-full rounded-md object-cover"
                                  />
                                ) : (
                                  <div className="pointer-events-none flex flex-col items-center justify-center px-3 sm:px-4 text-center">
                                    <ArrowUpTrayIcon className="mx-auto mb-2 size-5 sm:size-6 opacity-80" />
                                    <div className="font-medium leading-snug">
                                      Click or drag &amp; drop an image
                                    </div>
                                    <div className="mt-1 text-[10px] sm:text-xs text-gray-600/80 leading-snug">
                                      JPG/PNG/WebP/GIF/SVG, up to 5&nbsp;MB
                                    </div>
                                  </div>
                                )}

                                {dragActive && (
                                  <div className="pointer-events-none absolute inset-1 rounded-md border-2 border-dashed border-indigo-400/80 bg-indigo-100/30" />
                                )}

                                <input
                                  id="avatar-input"
                                  ref={fileInputRef}
                                  type="file"
                                  accept={ACCEPTED.join(",")}
                                  onChange={onPick}
                                  className="hidden"
                                />
                              </label>
                            </div>
                          </div>

                          {avatarError && (
                            <p className="mt-2 text-xs text-red-600">{avatarError}</p>
                          )}
                          {avatarPreview && (
                            <div className="mt-2">
                              <button
                                type="button"
                                onClick={clearAvatar}
                                className="rounded-md border px-2 py-1 text-xs"
                              >
                                Remove image
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Basic identity fields next to avatar */}
                        <div className="mt-6 sm:ml-6 sm:flex-1">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">
                                Username
                              </label>
                              <input
                                type="text"
                                value={client}
                                onChange={(e) => setClient(e.target.value)}
                                placeholder="ex. FirstName.LastName"
                                className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-gray-600">
                                Email
                              </label>
                              <input
                                type="email"
                                placeholder="ex. example@solidarity.com.bh"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                              />
                            </div>
                          </div>

                          {/* Temp Password (optional) */}
                          <div className="mt-3">
                            <label className="mb-1 block text-xs font-medium text-gray-600">
                              Temporary Password
                            </label>
                            <input
                              type="text"
                              placeholder="ex. Solid@1234"
                              value={tempPassword}
                              onChange={(e) => setTempPassword(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fields */}
                    <div className="px-4 py-5 sm:px-0 sm:py-0">
                      <dl className="space-y-8 sm:space-y-0 sm:divide-y sm:divide-gray-200">
                        {/* Department */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-48">
                            Department
                          </dt>
                          <dd className="mt-1 w-full sm:ml-6 sm:mt-0">
                            <input
                              placeholder="ex. IT"
                              type="text"
                              value={department}
                              onChange={(e) => setDepartment(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                            />
                          </dd>
                        </div>
                        {/* Position */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-48">
                            Position
                          </dt>
                          <dd className="mt-1 w-full sm:ml-6 sm:mt-0">
                            <input
                              type="text"
                              placeholder="ex. Software Engineer"
                              value={position}
                              onChange={(e) => setPosition(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                            />
                          </dd>
                        </div>
                        {/* Mobile */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-48">
                            Mobile
                          </dt>
                          <dd className="mt-1 w-full sm:ml-6 sm:mt-0">
                            <input
                              placeholder="ex. 36XXXXXX"
                              type="text"
                              value={phoneNumber}
                              onChange={(e) => setPhoneNumber(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                            />
                          </dd>
                        </div>
                        {/* Work Phone */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-48">
                            Work Phone
                          </dt>
                          <dd className="mt-1 w-full sm:ml-6 sm:mt-0">
                            <input
                              placeholder="ex. 17XXXXXX"
                              type="text"
                              value={ext}
                              onChange={(e) => setExt(e.target.value)}
                              className="block w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                            />
                          </dd>
                        </div>

                        {/* Sections */}
                        <div className="sm:flex sm:px-6 sm:py-5">
                          <dt className="text-sm font-medium text-gray-500 sm:w-48">
                            Sections
                          </dt>
                          <dd className="mt-1 w-full sm:ml-6 sm:mt-0">
                            <div className="relative h-full">
                              <div className="flex items-center justify-between p-3">
                                <div className="text-sm text-gray-600">
                                  Selected: {selectedCounts.parents} parents,{" "}
                                  {selectedCounts.children} children
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={clearSelection}
                                    className="rounded-md border px-3 py-1.5 text-sm"
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>

                              <nav aria-label="Hub sections" className="h-full overflow-y-auto">
                                <ul className="divide-y divide-gray-200">
                                  {lookupSections.map((parent) => {
                                    const parentSelected = selectedParents.has(parent.id);
                                    const parentChildren = parent.children ?? [];
                                    const isPaymentRequests =
                                      parent.productName === "Payment Requests";
                                    const isOpen = openRows.has(parent.id);

                                    return (
                                      <li key={parent.id} className="px-2 py-1">
                                        {parentChildren.length ? (
                                          <div className="rounded-md">
                                            {/* Header row */}
                                            <div
                                              className={[
                                                "flex items-center justify-between cursor-pointer rounded-md px-3 py-2 hover:bg-gray-50 border",
                                                parentSelected
                                                  ? "bg-green-50 border-green-300"
                                                  : "border-transparent",
                                              ].join(" ")}
                                              role="button"
                                              tabIndex={0}
                                              onClick={() => toggleRow(parent.id)}
                                              onKeyDown={(e) =>
                                                (e.key === "Enter" || e.key === " ") &&
                                                toggleRow(parent.id)
                                              }
                                            >
                                              <span className="font-medium text-gray-900">
                                                {parent.productName == "AdminMain" ? "Admin" : parent.productName}
                                              </span>

                                              <span className="flex items-center gap-2">
                                                {parentSelected && (
                                                  <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                                    Selected
                                                  </span>
                                                )}
                                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm text-gray-500">
                                                  {parentChildren.length}
                                                </span>

                                                {/* Settings button for Payment Requests when selected */}
                                                {isPaymentRequests && parentSelected && (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setPaymentSettingsOpen(true);
                                                    }}
                                                    className="rounded-md border px-2 py-1 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                                  >
                                                    Settings
                                                  </button>
                                                )}

                                                {/* Add/Remove */}
                                                <button
                                                  type="button"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    tryToggleParent(parent)(e);
                                                    if (!parentSelected) {
                                                      setOpenRows(
                                                        (prev) => new Set(prev).add(parent.id)
                                                      );
                                                    }
                                                  }}
                                                  className={[
                                                    "rounded-md border px-2 py-1 text-xs",
                                                    parentSelected
                                                      ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                                                      : "text-green-700 border-green-200 hover:bg-green-50",
                                                  ].join(" ")}
                                                >
                                                  {parentSelected ? "Remove" : "Add"}
                                                </button>

                                                {/* Chevron */}
                                                <svg
                                                  className={[
                                                    "size-4 text-gray-500 transition-transform",
                                                    isOpen ? "rotate-180" : "",
                                                  ].join(" ")}
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                >
                                                  <path
                                                    d="M6 9l6 6 6-6"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  />
                                                </svg>
                                              </span>
                                            </div>

                                            {/* Panel */}
                                            {isOpen && (
                                              <div className="mt-2">
                                                <ul className="space-y-1 pl-6">
                                                  {parentChildren.map((child) => {
                                                    const isChildSelected =
                                                      selectedChildren.has(child.id);
                                                    return (
                                                      <li
                                                        key={child.id}
                                                        className={[
                                                          "flex items-center justify-between rounded-md",
                                                          isChildSelected
                                                            ? "bg-green-50 ring-1 ring-green-300"
                                                            : "",
                                                        ].join(" ")}
                                                      >
                                                        <div className="flex-1 rounded-md px-2 py-1.5 text-sm">
                                                          {child.productName == "PaymentRequestsAdmin" ? "User Management" : child.productName}
                                                        </div>
                                                        <button
                                                          type="button"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleChild(child.id, parent.id);
                                                          }}
                                                          className={[
                                                            "ml-2 rounded-md border px-2 py-1 text-xs",
                                                            isChildSelected
                                                              ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                                                              : "text-green-700 border-green-200 hover:bg-green-50",
                                                          ].join(" ")}
                                                        >
                                                          {isChildSelected ? "Remove" : "Add"}
                                                        </button>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          // no-children case
                                          <div
                                            className={[
                                              "flex items-center justify-between rounded-md px-3 py-2 border",
                                              parentSelected
                                                ? "bg-green-50 border-green-300"
                                                : "border-transparent",
                                            ].join(" ")}
                                          >
                                            <span className="font-medium text-gray-900">
                                              {parent.productName}
                                            </span>
                                            <div className="flex items-center gap-2">
                                              {parentSelected && (
                                                <>
                                                  <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                                    Selected
                                                  </span>
                                                  {isPaymentRequests && (
                                                    <button
                                                      type="button"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPaymentSettingsOpen(true);
                                                      }}
                                                      className="rounded-md border px-2 py-1 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                                                    >
                                                      Settings
                                                    </button>
                                                  )}
                                                </>
                                              )}
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  tryToggleParent(parent)(e);
                                                  if (!parentSelected) {
                                                    setOpenRows(
                                                      (prev) => new Set(prev).add(parent.id)
                                                    );
                                                  }
                                                }}
                                                className={[
                                                  "rounded-md border px-2 py-1 text-xs",
                                                  parentSelected
                                                    ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                                                    : "text-green-700 border-green-200 hover:bg-green-50",
                                                ].join(" ")}
                                              >
                                                {parentSelected ? "Remove" : "Add"}
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </nav>

                              {/* Confirm turning OFF a parent with selected children */}
                              {confirmParent && (
                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                                  <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
                                    <h2 className="text-lg font-semibold text-gray-900">
                                      Remove “{confirmParent.productName}” and all its
                                      selected sub-sections?
                                    </h2>
                                    <p className="mt-2 text-sm text-gray-600">
                                      This will remove access to the parent and any
                                      selected child items under it.
                                    </p>
                                    <div className="mt-6 flex justify-end gap-2">
                                      <button
                                        onClick={cancelTurnOffParent}
                                        className="rounded-md border px-4 py-2 text-sm"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={confirmTurnOffParent}
                                        className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
                                      >
                                        Yes, remove access
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </dd>
                        </div>
                      </dl>
                    </div>

                    {/* Footer */}
                    <div className="shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={handleCancel}
                          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveClick}
                          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Save confirmation popup — TRUE Headless UI Dialog */}
      <Dialog
        open={confirmSaveOpen}
        onClose={() => {}} // don't auto-close on backdrop/focus; only buttons close it
        className="relative z-[120]"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/40" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Create this user?
            </DialogTitle>
            <p className="mt-2 text-sm text-gray-600">
              This will create a new account with the selected access and profile details.
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-gray-600">
              <div>
                <span className="font-medium text-gray-800">Client:</span>{" "}
                {client || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Email:</span>{" "}
                {email || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Department:</span>{" "}
                {department || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Position:</span>{" "}
                {position || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Mobile:</span>{" "}
                {phoneNumber || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Work Phone:</span>{" "}
                {ext || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Limit:</span>{" "}
                {requestLimit || "—"}
              </div>
              <div>
                <span className="font-medium text-gray-800">Access:</span>{" "}
                {paymentAccess}
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-800">Types:</span>{" "}
                {paymentRequestTypes.length
                  ? paymentRequestTypes.join(", ")
                  : "—"}
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-800">Selected:</span>{" "}
                {selectedCounts.parents} parent(s), {selectedCounts.children} child(ren)
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleSaveCancel}
                className="rounded-md border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfirm}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700"
              >
                Yes, Create
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Payment Requests Settings popup — TRUE Headless UI Dialog */}
      <Dialog
        open={paymentSettingsOpen && isPaymentRequestsSelected}
        onClose={() => setPaymentSettingsOpen(false)}
        className="relative z-[110]"
      >
        <DialogBackdrop className="fixed inset-0 bg-black/40" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Payment Requests — Settings
              </DialogTitle>
              <button
                type="button"
                onClick={() => setPaymentSettingsOpen(false)}
                className="rounded-md text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                <XMarkIcon className="size-6" />
              </button>
            </div>

            {/* Limit */}
            <div className="mt-4 sm:flex sm:items-center sm:gap-6">
              <label className="block text-sm font-medium text-gray-600 sm:w-56">
                Request Limit
              </label>
              <input
                type="number"
                placeholder="ex. 1000"
                value={requestLimit}
                onChange={(e) => setRequestLimit(e.target.value)}
                className="mt-1 w-full rounded-md bg-white px-3 py-1.5 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:outline-indigo-600 sm:mt-0"
              />
            </div>

            {/* Access Level */}
            <div className="mt-4">
              <legend className="block text-sm font-medium text-gray-600">
                Access Level
              </legend>
              <div className="mt-2 space-y-2">
                {[
                  { value: "OwnOnly", label: "View Own Only" },
                  { value: "AllView", label: "View All" },
                  { value: "AllClaims", label: "View All Claims" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className="flex w-full items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-white cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="payment-access"
                      value={opt.value}
                      checked={paymentAccess === opt.value}
                      onChange={(e) => setPaymentAccess(e.target.value)}
                      className="size-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span className="text-sm text-gray-900">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Types */}
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-600">
                Request Type
              </label>
              <fieldset className="grid gap-2 sm:grid-cols-2">
                {[
                  { val: "Policy", label: "Policy" },
                  { val: "General Payment", label: "General Payment" },
                  { val: "deductible", label: "Deductible (Claim)" },
                  { val: "depreciation", label: "Depreciation (Claim)" },
                  { val: "ActivePolicyRecurring", label: "Active Policy for recurring (Life)" },
                ].map(({ val, label }) => (
                  <label
                    key={val}
                    className="flex items-center gap-3 rounded-md border border-gray-200 p-3 hover:bg-white"
                  >
                    <input
                      type="checkbox"
                      className="size-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      checked={paymentRequestTypes.includes(val)}
                      onChange={() => togglePaymentRequestType(val)}
                    />
                    <span className="text-sm text-gray-900">{label}</span>
                  </label>
                ))}
              </fieldset>
              <p className="mt-2 text-xs text-gray-500">Select one or more types.</p>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setPaymentSettingsOpen(false)}
                className="rounded-md border px-4 py-2 text-sm"
              >
                Close
              </button>
              <button
                onClick={() => setPaymentSettingsOpen(false)}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
